// The pane machinery, lifted out of `reading-view.tsx` so the reader and the
// coming `/admin` editor workbench render ONE implementation of it: the text-size
// bars, the retelling and original `DocumentPane`s, the narrow-screen
// `InterleavedColumn`, and the two scroll-sync hooks that keep the panes
// together.
//
// Two rules make this shareable rather than merely moved, and both are
// load-bearing:
//
//  1. It NEVER calls `useMediaQuery`. The caller decides `interleaved` and
//     `showPaneBars`. That is what lets the workbench put two panes side by side
//     at any viewport while the reader keeps its 767px breakpoint behaviour — a
//     component that measured the window could only ever do one of those.
//  2. It NEVER calls `setPref`. Font size is a controlled prop, reported upward
//     through `onFontChange`. The reader persists that; the workbench must not
//     (an admin cranking the original to 24px to read Homer is not restating
//     their own reading preferences).
//
// Reader-only concerns deliberately left behind in `reading-view.tsx`: the
// bookmarks menu, `PageTurnNav` (injected here as `retellingFooter`, so this
// file never imports it), the copyright footer and copy handler, the premium
// gate, and the `?original=`/`?sync=` URL round-tripping.

import { type ReactNode, type RefObject } from "react";
import { Link2, Unlink } from "lucide-react";
import { Toggle } from "@bindery/ui/components/ui/toggle";
import type { CorpusAnchorPair } from "@bindery/shared/corpus/anchors";
import type { DocumentModel } from "@bindery/shared/corpus/document";
import type {
  FontSizePrefs,
  PaneId,
  SyncMode,
} from "@bindery/shared/corpus/reader";
import { cn } from "@/lib/utils";
import { DocumentPane } from "./document-pane";
import { FontSizeControl } from "./font-size-control";
import { InterleavedColumn } from "./interleaved-column";
import {
  ReadingPanesContext,
  useReadingPanes,
  type ReadingPanesContextValue,
} from "./reading-panes-context";
import { useHardAlign } from "./use-hard-align";
import { useSyncScroll } from "./use-sync-scroll";

/** Module constant, not an inline default: an inline `() => {}` would be a new
 *  identity every render (vercel-react-best-practices §5.4). */
const NO_EXPAND = (): void => undefined;

/** Apply one pane's new size, honouring the `linked` flag. Pure. */
function resolvePaneFont(
  prefs: FontSizePrefs,
  pane: PaneId,
  value: number,
): FontSizePrefs {
  return prefs.linked
    ? { retelling: value, original: value, linked: true }
    : { ...prefs, [pane]: value };
}

/**
 * Tie the two sizes together, or cut them loose. Pure, and beside
 * `resolvePaneFont` because it is the same transform's other half: linking
 * unifies both panes to the RETELLING's size, since the retelling is the pane
 * being read and so the one whose size should survive the merge.
 */
function resolveLinked(prefs: FontSizePrefs, linked: boolean): FontSizePrefs {
  return linked
    ? { retelling: prefs.retelling, original: prefs.retelling, linked: true }
    : { ...prefs, linked: false };
}

/**
 * The slim, non-scrolling, translucent band a text-size bar lives in — and,
 * load-bearing, a flex ROW. `PaneBarHalf` is `flex-1`, which in the caller's
 * flex COLUMN would grow the bar to fill the viewport; it has to be a row's
 * child to mean "half the width".
 */
function FontBarRow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-stretch border-b border-border/70 bg-background/80 backdrop-blur",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * One pane's slice of the text-size bar: the pane's name over the column it
 * sizes, and the size control at the column's INNER edge.
 *
 * `side` is which half of a two-pane bar this is. `"right"` is the original's,
 * and it is a REFLECTION of the left one — so both controls end up flanking
 * the link toggle on the divide, and the toggle reads as the clasp between the
 * two things it links rather than as a third loose button in a toolbar
 * somewhere above.
 *
 * `"solo"` is the desktop bar with the original closed. There is no divide to
 * mirror about and no toggle to leave room for, so the label goes back to the
 * left edge and the control to the right: centring one label over an empty
 * 1600px bar just looks like half of a layout that lost its other half.
 *
 * In the two-pane halves the label is centred by equal flexible margins rather
 * than by absolute positioning, so a pane too narrow to afford both a centred
 * label AND the control slides the label off centre instead of colliding.
 *
 * Reads the size and the setter from context rather than taking them as props:
 * this renders in three different places and would otherwise drill the same
 * three values through each (composition policy §1.2).
 */
function PaneBarHalf({
  label,
  pane,
  side = "solo",
  className,
}: {
  label: string;
  pane: PaneId;
  side?: "left" | "right" | "solo";
  className?: string;
}) {
  const { state, actions } = useReadingPanes();
  const mirrored = side === "right";
  const spacer = <span className="flex-1" aria-hidden="true" />;
  const control = (
    <span
      className={cn("flex flex-1", mirrored ? "justify-start" : "justify-end")}
    >
      <FontSizeControl
        label={`${label} text size`}
        value={state.fontSize[pane]}
        onChange={(n) => {
          actions.setPaneFont(pane, n);
        }}
        mirrored={mirrored}
      />
    </span>
  );
  const name = (
    <span className="text-muted-foreground shrink-0 font-serif text-xs tracking-wide">
      {label}
    </span>
  );

  return (
    <div
      className={cn(
        "flex min-w-0 flex-1 items-center gap-3 py-2",
        // The inner edge keeps clear of the link toggle straddling the divide;
        // the solo bar has no toggle, so it keeps an even margin.
        side === "right" && "pr-4 pl-7",
        side === "left" && "pr-7 pl-4",
        side === "solo" && "px-4",
        className,
      )}
    >
      {mirrored ? control : side === "solo" ? name : spacer}
      {side === "solo" ? spacer : name}
      {mirrored ? spacer : control}
    </div>
  );
}

/**
 * The lone unlabelled control, for the single-column layouts. Hidden per-pane
 * sliders would have nothing to mean with one column, but text size is not a
 * two-pane convenience — it is how a low-vision reader reads at all, and this
 * is then the only size affordance on the page. Centred rather than
 * right-aligned: a lone slider on the right hugs the phone's edge, and the
 * centre is also where the thumb is.
 */
function LoneFontBar({ pane }: { pane: PaneId }) {
  const { state, actions } = useReadingPanes();
  return (
    <FontBarRow className="items-center justify-center px-4 py-2">
      <FontSizeControl
        label="Text size"
        value={state.fontSize[pane]}
        onChange={(n) => {
          actions.setPaneFont(pane, n);
        }}
      />
    </FontBarRow>
  );
}

/**
 * The two-pane text-size bar: mirrored halves either side of the divide, with
 * the link toggle sitting ON the divide between them.
 *
 * The toggle is absolutely centred rather than laid out as a third flex child
 * so that the halves stay a true 50/50 — the panes below are two `flex-1`
 * columns, and a toggle taking width in this row would shift each label a few
 * pixels off the column it names. It is written BETWEEN the two halves rather
 * than after them so the DOM order matches the reading order: absolute
 * positioning does not care where it sits, but tab order does.
 */
function TwoPaneFontBar() {
  const { state, actions } = useReadingPanes();
  return (
    <FontBarRow className="relative">
      <PaneBarHalf label="Retelling" pane="retelling" side="left" />
      <div className="border-border bg-background absolute top-1/2 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2 rounded-full border p-0.5 shadow-sm">
        <Toggle
          size="sm"
          // The `FontSizeControl` buttons either side of it get a coarse-pointer
          // minimum; the control between them must not be the one 28px target
          // in the row (WCAG 2.5.5). The workbench renders this bar at ANY
          // viewport, so "desktop only" is not a defence.
          className="size-7 min-w-0 rounded-full p-0 pointer-coarse:size-11"
          pressed={state.fontSize.linked}
          onPressedChange={actions.setLinked}
          aria-label="Link both panes' text size"
          title="Link text sizes"
        >
          {state.fontSize.linked ? (
            <Link2 className="size-4" />
          ) : (
            <Unlink className="size-4" />
          )}
        </Toggle>
      </div>
      <PaneBarHalf
        label="Original"
        pane="original"
        side="right"
        className="border-border border-l"
      />
    </FontBarRow>
  );
}

interface ReadingPanesProps {
  retellingModel: DocumentModel;
  originalModel: DocumentModel;
  anchors: readonly CorpusAnchorPair[];
  /**
   * Created by the CALLER, not here: the route needs the same nodes for
   * `useReadingNav`, `useCopyHandler` and `SelectionLayer`, and a parent cannot
   * consume a context its own child provides. So these are props.
   */
  retellingRef: RefObject<HTMLDivElement | null>;
  originalRef: RefObject<HTMLDivElement | null>;
  /** The original is open — as a second pane OR woven in. */
  showOriginal: boolean;
  /**
   * Weave the original INTO the retelling as one scroll column instead of
   * opening a second pane. The caller decides (the reader: `showOriginal &&
   * isNarrow`) — see rule 1 in the file header.
   */
  interleaved: boolean;
  syncMode: SyncMode;
  /** Controlled. Never persisted here — see rule 2 in the file header. */
  fontSize: FontSizePrefs;
  onFontChange: (next: FontSizePrefs, pane: PaneId) => void;
  /**
   * Label each pane's bar ("Retelling"/"Original") instead of showing ONE
   * unlabelled bar above the column. Cannot be derived from `interleaved`:
   * narrow-with-the-original-closed is not interleaved, yet still wants the
   * single unlabelled bar. The reader passes `!isNarrow`; the workbench passes
   * `true`.
   *
   * Required, with no default: this is the one layout decision this
   * component deliberately does not make for itself (rule 1 above — it never
   * calls `useMediaQuery`), so there is no value that is safe to assume on a
   * caller's behalf. A forgetful caller should get a type error, not a
   * silent narrow-phone layout.
   */
  showPaneBars: boolean;
  /** Non-English source — gates the gloss entry points. */
  glossEligible: boolean;
  /** A reader opened an interleaved stretch (`reading.original_expand`). */
  onExpandOriginal?: () => void;
  /**
   * Rendered after the last block of the retelling column — the reader's
   * `PageTurnNav`. Suppressed while the original is open, exactly as before:
   * the sync hooks own the pane's geometry then.
   */
  retellingFooter?: ReactNode;
  /** Applied to the pane ROW. Omit and the row is what it always was. */
  className?: string;
}

export function ReadingPanes({
  retellingModel,
  originalModel,
  anchors,
  retellingRef,
  originalRef,
  showOriginal,
  interleaved,
  syncMode,
  fontSize,
  onFontChange,
  showPaneBars,
  glossEligible,
  onExpandOriginal = NO_EXPAND,
  retellingFooter,
  className,
}: ReadingPanesProps) {
  // Two independent scroll panes to sync. Both sync hooks are OFF in the
  // interleaved layout: there is a single scroll container there, so there is
  // no follower pane to drive.
  const twoPane = showOriginal && !interleaved;
  // Opaque remeasure token. `showPaneBars` is in it because a bar appearing or
  // disappearing changes the panes' height, and a stale measurement of that is
  // exactly what makes hard alignment drift. This replaces the old key's
  // `isNarrow` term, which this component no longer knows: in the reader
  // `showPaneBars` is `!isNarrow` and `interleaved` carries the rest, so the
  // key still changes on precisely the transitions it used to.
  const layoutKey = `${String(showOriginal)}:${String(interleaved)}:${String(showPaneBars)}:${syncMode}`;

  useSyncScroll({
    retellingRef,
    originalRef,
    retellingModel,
    originalModel: twoPane ? originalModel : null,
    anchors,
    enabled: twoPane && syncMode === "interpolated",
    retellingFontSize: fontSize.retelling,
    originalFontSize: fontSize.original,
    layoutKey,
  });
  useHardAlign({
    retellingRef,
    originalRef,
    retellingModel,
    originalModel: twoPane ? originalModel : null,
    anchors,
    enabled: twoPane && syncMode === "hard",
    retellingFontSize: fontSize.retelling,
    originalFontSize: fontSize.original,
    layoutKey,
  });

  const context: ReadingPanesContextValue = {
    state: {
      retellingModel,
      originalModel,
      anchors,
      showOriginal,
      interleaved,
      twoPane,
      syncMode,
      fontSize,
      glossEligible,
    },
    actions: {
      setPaneFont: (pane, value) => {
        onFontChange(resolvePaneFont(fontSize, pane, value), pane);
      },
      setLinked: (linked) => {
        onFontChange(resolveLinked(fontSize, linked), "retelling");
      },
    },
    meta: { retellingRef, originalRef, layoutKey },
  };

  // A fragment, not a wrapper: the bar and the pane row are siblings in the
  // caller's flex column, and interposing a div would change the scroll
  // geometry that `min-h-0`/`flex-1` are doing the real work in.
  return (
    <ReadingPanesContext value={context}>
      {/* The bar(s), hoisted ABOVE the pane row rather than tucked inside each
          pane column. That is what lets the two-pane bar span the divide and
          hang the link toggle on it; the pane columns below are unchanged
          50/50 `flex-1` siblings either way, so the scroll geometry is the
          same as when each column carried its own bar.

          The lone control drives the retelling size, which in the interleaved
          layout is the size BOTH texts render at: the original is the harder
          text there, so scaling it with the prose around it is the point rather
          than a side effect. */}
      {!showPaneBars && <LoneFontBar pane="retelling" />}
      {showPaneBars &&
        !interleaved &&
        (twoPane ? (
          <TwoPaneFontBar />
        ) : (
          <FontBarRow>
            <PaneBarHalf label="Retelling" pane="retelling" />
          </FontBarRow>
        ))}

      <div className={cn("flex min-h-0 flex-1", className)}>
        {interleaved ? (
          <InterleavedColumn
            ref={retellingRef}
            retelling={retellingModel}
            original={originalModel}
            anchors={anchors}
            // One size for both texts here; the per-pane sizes and the link
            // flag are left untouched for the two-pane layout.
            fontSize={fontSize.retelling}
            glossEligible={glossEligible}
            onExpandOriginal={onExpandOriginal}
            className="flex-1"
            footer={retellingFooter}
          />
        ) : (
          <>
            <div className="flex min-w-0 flex-1 flex-col">
              <DocumentPane
                ref={retellingRef}
                model={retellingModel}
                pane="retelling"
                fontSize={fontSize.retelling}
                className="flex-1"
                // Footer only in single-column reading: while the original is
                // open the sync hooks own this pane's geometry (`showOriginal`
                // is part of `layoutKey`, so toggling re-measures).
                footer={showOriginal ? undefined : retellingFooter}
              />
            </div>

            {twoPane && (
              <div className="border-border flex min-w-0 flex-1 flex-col border-l">
                <DocumentPane
                  ref={originalRef}
                  model={originalModel}
                  pane="original"
                  fontSize={fontSize.original}
                  glossEntry={glossEligible}
                  className="flex-1"
                />
              </div>
            )}
          </>
        )}
      </div>
    </ReadingPanesContext>
  );
}
